import type {
  BoostDraft,
  CheckinData,
  UpdaterStatus,
  UserSession,
} from '@shared/types';
import type { AssistantConversation } from '@shared/assistant-conversations';
import type { AppPreferences, NavTarget, PlatformServices } from './types';
import { DEFAULT_PREFERENCES, IDLE_UPDATER_STATUS } from './common';

const CURRENT_APP_VERSION = import.meta.env.VITE_APP_VERSION || 'web';
const CURRENT_BUILD_ID = import.meta.env.VITE_APP_BUILD_ID || CURRENT_APP_VERSION;
const WEB_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const STORAGE_KEYS = {
  session: 'happily-teams:web:session',
  drafts: 'happily-teams:web:drafts',
  assistantConversations: 'happily-teams:web:assistant-conversations',
  preferences: 'happily-teams:web:preferences',
  checkinData: 'happily-teams:web:checkin-data',
  feedbackLastSeen: 'happily-teams:web:feedback-last-seen',
} as const;

interface WebAppShellMetadata {
  version: string | null;
  buildId: string | null;
  assetUrls: Set<string>;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures in browser-restricted contexts.
  }
}

function removeItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage failures in browser-restricted contexts.
  }
}

function readDomWindowActivity(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

function normalizeAssetUrl(value: string): string {
  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value;
  }
}

function readCurrentAssetUrls(): Set<string> {
  const urls = new Set<string>();
  document.querySelectorAll<HTMLScriptElement>('script[src]').forEach((script) => {
    urls.add(normalizeAssetUrl(script.getAttribute('src') || ''));
  });
  document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]').forEach((link) => {
    urls.add(normalizeAssetUrl(link.getAttribute('href') || ''));
  });
  return urls;
}

export function parseWebAppShellMetadata(html: string): WebAppShellMetadata {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const version = doc.querySelector<HTMLMetaElement>('meta[name="happily-app-version"]')?.content?.trim() || null;
  const buildId = doc.querySelector<HTMLMetaElement>('meta[name="happily-build-id"]')?.content?.trim() || null;
  const assetUrls = new Set<string>();

  doc.querySelectorAll<HTMLScriptElement>('script[src]').forEach((script) => {
    assetUrls.add(normalizeAssetUrl(script.getAttribute('src') || ''));
  });
  doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]').forEach((link) => {
    assetUrls.add(normalizeAssetUrl(link.getAttribute('href') || ''));
  });

  return { version, buildId, assetUrls };
}

export function hasWebAppUpdate(
  metadata: WebAppShellMetadata,
  current: {
    version?: string;
    buildId?: string;
    assetUrls?: Set<string>;
  } = {},
): boolean {
  const currentVersion = current.version || CURRENT_APP_VERSION;
  const currentBuildId = current.buildId || CURRENT_BUILD_ID;
  const currentAssetUrls = current.assetUrls || readCurrentAssetUrls();

  if (metadata.buildId && currentBuildId && metadata.buildId !== currentBuildId) {
    return true;
  }

  if (metadata.version && currentVersion && metadata.version !== currentVersion) {
    return true;
  }

  if (metadata.assetUrls.size > 0 && currentAssetUrls.size > 0) {
    return [...metadata.assetUrls].some((url) => !currentAssetUrls.has(url));
  }

  return false;
}

function getWebAppUpdateId(metadata: WebAppShellMetadata): string {
  return metadata.buildId || metadata.version || [...metadata.assetUrls].sort().join('|') || 'web-update';
}

function createWebUpdater(): PlatformServices['updater'] {
  let status: UpdaterStatus = IDLE_UPDATER_STATUS;
  let checkPromise: Promise<UpdaterStatus> | null = null;
  let intervalId: number | null = null;
  let visibilityListener: (() => void) | null = null;
  let focusListener: (() => void) | null = null;
  const listeners = new Set<(status: UpdaterStatus) => void>();

  const emit = (nextStatus: UpdaterStatus) => {
    status = nextStatus;
    listeners.forEach((listener) => listener(nextStatus));
  };

  const checkForUpdates = async (): Promise<UpdaterStatus> => {
    if (checkPromise) {
      return checkPromise;
    }

    checkPromise = (async () => {
      try {
        const url = new URL('/', window.location.origin);
        url.searchParams.set('__happily_update_check', Date.now().toString());

        const response = await fetch(url.toString(), { cache: 'no-store' });
        if (!response.ok) {
          return status;
        }

        const metadata = parseWebAppShellMetadata(await response.text());
        if (!hasWebAppUpdate(metadata)) {
          if (status.state !== 'idle') {
            emit(IDLE_UPDATER_STATUS);
          }
          return status;
        }

        const updateId = getWebAppUpdateId(metadata);
        if (status.state === 'downloaded' && status.updateId === updateId) {
          return status;
        }

        const nextStatus: UpdaterStatus = {
          state: 'downloaded',
          version: metadata.version || CURRENT_APP_VERSION,
          updateId,
        };
        emit(nextStatus);
        return nextStatus;
      } catch (error) {
        console.warn('[WebUpdater] Failed to check for web updates', error);
        return status;
      } finally {
        checkPromise = null;
      }
    })();

    return checkPromise;
  };

  const runCheck = () => {
    void checkForUpdates();
  };

  const startPolling = () => {
    if (intervalId !== null) {
      return;
    }

    intervalId = window.setInterval(runCheck, WEB_UPDATE_CHECK_INTERVAL_MS);
    visibilityListener = () => {
      if (document.visibilityState === 'visible') {
        runCheck();
      }
    };
    focusListener = runCheck;
    document.addEventListener('visibilitychange', visibilityListener);
    window.addEventListener('focus', focusListener);
    runCheck();
  };

  const stopPolling = () => {
    if (listeners.size > 0) {
      return;
    }

    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    if (visibilityListener) {
      document.removeEventListener('visibilitychange', visibilityListener);
      visibilityListener = null;
    }
    if (focusListener) {
      window.removeEventListener('focus', focusListener);
      focusListener = null;
    }
  };

  return {
    getStatus: async () => status,
    onStatus: (callback) => {
      listeners.add(callback);
      startPolling();
      return () => {
        listeners.delete(callback);
        stopPolling();
      };
    },
    checkForUpdates,
    restartToInstall: async () => {
      window.location.reload();
    },
    setAuthToken: async () => {},
  };
}

export function createWebPlatformServices(): PlatformServices {
  const notificationClickListeners = new Set<(data: any) => void>();
  const navigateListeners = new Set<(tab: NavTarget) => void>();
  const badgeListeners = new Set<(count: number) => void>();
  let badgeCount = 0;
  const updater = createWebUpdater();

  const emitNotificationClick = (data: any) => {
    notificationClickListeners.forEach((listener) => listener(data));
    if (data?.tab) {
      navigateListeners.forEach((listener) => listener(data.tab));
    }
  };

  const createBrowserNotification = async (title: string, body: string, data?: any) => {
    if (!('Notification' in window)) return;
    let permission = window.Notification.permission;

    if (permission === 'default') {
      permission = await window.Notification.requestPermission();
    }

    if (permission !== 'granted') {
      return;
    }

    const notification = new window.Notification(title, { body });
    notification.onclick = () => {
      window.focus();
      emitNotificationClick(data);
      notification.close();
    };
  };

  return {
    target: 'web',
    os: 'web',
    storage: {
      getSession: async () => readJson<UserSession | null>(STORAGE_KEYS.session, null),
      setSession: async (session) => {
        writeJson(STORAGE_KEYS.session, session);
      },
      clearSession: async () => {
        removeItem(STORAGE_KEYS.session);
      },
      getDrafts: async () => readJson<Record<string, BoostDraft>>(STORAGE_KEYS.drafts, {}),
      setDrafts: async (drafts) => {
        writeJson(STORAGE_KEYS.drafts, drafts);
      },
      // One map under one key, keyed by pipe-encoded email — a shared browser profile
      // must not hand one manager's assistant history to the next person who signs in.
      getAssistantConversations: async (userKey) =>
        readJson<Record<string, AssistantConversation[]>>(STORAGE_KEYS.assistantConversations, {})[userKey] ?? [],
      setAssistantConversations: async (userKey, conversations) => {
        const all = readJson<Record<string, AssistantConversation[]>>(STORAGE_KEYS.assistantConversations, {});
        writeJson(STORAGE_KEYS.assistantConversations, { ...all, [userKey]: conversations });
      },
      getPreferences: async () => ({
        ...DEFAULT_PREFERENCES,
        ...readJson<AppPreferences>(STORAGE_KEYS.preferences, {}),
      }),
      setPreferences: async (prefs) => {
        writeJson(STORAGE_KEYS.preferences, prefs);
      },
      getCheckinData: async () => readJson<CheckinData | null>(STORAGE_KEYS.checkinData, null),
      setCheckinData: async (data) => {
        writeJson(STORAGE_KEYS.checkinData, data);
      },
      getFeedbackLastSeen: async () => readJson<Record<string, string>>(STORAGE_KEYS.feedbackLastSeen, {}),
      setFeedbackLastSeen: async (docId, timestamp) => {
        const current = readJson<Record<string, string>>(STORAGE_KEYS.feedbackLastSeen, {});
        current[docId] = timestamp;
        writeJson(STORAGE_KEYS.feedbackLastSeen, current);
      },
    },
    auth: {
      openExternalUrl: async (url) => {
        window.location.assign(url);
      },
      onDeepLink: () => () => {},
    },
    notifications: {
      updateBadge: (count) => {
        badgeCount = count;
        badgeListeners.forEach((listener) => listener(count));
      },
      updateTrayData: () => {
        // No tray concept on web.
      },
      send: async (title, body, data) => {
        await createBrowserNotification(title, body, data);
      },
      onNotificationClicked: (callback) => {
        notificationClickListeners.add(callback);
        return () => notificationClickListeners.delete(callback);
      },
      onNavigateToTab: (callback) => {
        navigateListeners.add(callback);
        return () => navigateListeners.delete(callback);
      },
      onBadgeUpdated: (callback) => {
        badgeListeners.add(callback);
        callback(badgeCount);
        return () => badgeListeners.delete(callback);
      },
      isSupported: async () => 'Notification' in window,
    },
    window: {
      minimize: () => {},
      maximize: () => {},
      close: () => {},
      isMaximized: async () => false,
      getActivityState: async () => readDomWindowActivity(),
      onActivityChanged: (callback) => {
        const syncFromDom = () => callback(readDomWindowActivity());
        document.addEventListener('visibilitychange', syncFromDom);
        window.addEventListener('focus', syncFromDom);
        window.addEventListener('blur', syncFromDom);
        return () => {
          document.removeEventListener('visibilitychange', syncFromDom);
          window.removeEventListener('focus', syncFromDom);
          window.removeEventListener('blur', syncFromDom);
        };
      },
    },
    device: {
      triggerHaptic: () => {},
      setAutoLaunch: async () => {},
      getAutoLaunch: async () => false,
    },
    updater,
  };
}
